import { AxiosResponse, isAxiosError } from 'axios';

type TRetryOptions = {
  maxRetries?: number;
  initialDelayMs?: number;
  onRetry?: (attempt: number, error: unknown) => void;
};

const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_INITIAL_DELAY_MS = 1000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const withNetworkRetry = async <T>(
  executor: () => Promise<AxiosResponse<T>>,
  options: TRetryOptions = {}
): Promise<AxiosResponse<T>> => {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
    onRetry
  } = options;

  let delayMs = initialDelayMs;
  const totalAttempts = maxRetries + 1; // initial try + retries

  for (let attemptIndex = 0; attemptIndex < totalAttempts; attemptIndex += 1) {
    try {
      return await executor();
    } catch (error) {
      const axiosError = isAxiosError(error);
      const isNetworkError = axiosError && error.code === 'ERR_NETWORK';
      const hasRetryBudget = attemptIndex < maxRetries;

      if (!isNetworkError || !hasRetryBudget) {
        throw error;
      }

      const nextAttemptNumber = attemptIndex + 1; // 1-based attempt count for logging
      if (typeof onRetry === 'function') {
        try {
          onRetry(nextAttemptNumber, error);
        } catch (_callbackError) {
          // ignore callback errors so we can continue retrying
        }
      }

      await sleep(delayMs);
      delayMs *= 2;
    }
  }

  throw new Error('withNetworkRetry exhausted retry attempts');
};
